import React, { useState, useEffect, useRef } from "react";
import ReactTooltip from "react-tooltip";
import styled from "styled-components";
import colors from "styles/colors";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import googleActions from "modules/google/actions";
import googleSelectors from "modules/google/selectors";
import useWindowSize from "layout/useWindowSize";

const GoogleAdsFormList = ({ errorStatus, action, leadFormId }) => {
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const googleAdsLeadFormResult = useSelector((state) => googleSelectors.getGoogleAdsLeadFormResult(state));
  const duplicateGoogleFormResult = useSelector((state) => googleSelectors.setDuplicateGoogleFormResult(state));
  const setCheckedGoogleAdsFormItemsResult = useSelector((state) =>
    googleSelectors.setCheckedGoogleAdsFormItemsResult(state),
  );
  const changeGoogleAdsFormSelectTypeResult = useSelector((state) =>
    googleSelectors.changeGoogleAdsFormSelectTypeResult(state),
  );
  const [checkedItems, setCheckedItems] = useState([]);
  const [selectedRadioValue, setSelectedRadioValue] = useState();
  const windowSize = useWindowSize();

  const infoRef = useRef();
  const [tooltipPlace, setTooltipPlace] = useState("right");

  useEffect(() => {
    const info = infoRef.current;
    if (info) {
      const infoRect = info.getBoundingClientRect();
      const rightOfInfo = windowSize.width - infoRect.right;

      if (rightOfInfo < 200) {
        setTooltipPlace("bottom");
      } else {
        setTooltipPlace("right");
      }
    }
  }, [windowSize]);

  useEffect(() => {
    const savedValue = localStorage.getItem("googleAdsFormSelectType");
    if (savedValue) {
      setSelectedRadioValue(savedValue);
    } else {
      setSelectedRadioValue("all");
    }
  }, [localStorage.getItem("googleAdsFormSelectType")]);

  useEffect(() => {
    let formList;
    if (changeGoogleAdsFormSelectTypeResult.data.selectType === "all") {
      formList = null;
    } else if (changeGoogleAdsFormSelectTypeResult.data.selectType === "select") {
      formList = setCheckedGoogleAdsFormItemsResult.data.items;
    }

    if (formList) {
      setCheckedItems(formList);
    } else if (formList === null) {
      setCheckedItems([]);
    }
  }, [changeGoogleAdsFormSelectTypeResult, setCheckedGoogleAdsFormItemsResult]);

  /** action when select custom radio */
  const handleSelect = async (type) => {
    let newValue = type;
    setSelectedRadioValue(newValue);
    localStorage.setItem("googleAdsFormSelectType", newValue);
    await dispatch(googleActions.changeGoogleAdsFormSelectType(type));
    await dispatch(googleActions.setValidateGoogleLeadForm({ result: false }));
    if (type == "all") {
      handleSelectAll();
    } else if (type == "select") {
      setCheckedItems([]);
      await dispatch(googleActions.setCheckedGoogleAdsFormItems([]));
    }
  };

  /** action when check box
   * @param {string} itemId
   * @param {object} event
   */
  const handleOnChange = async (itemId, event) => {
    let newCheckedItems;
    if (event.target.checked) {
      newCheckedItems = [...checkedItems, itemId];
    } else {
      newCheckedItems = checkedItems.filter((item) => item !== itemId);
    }
    setCheckedItems(newCheckedItems);
    await dispatch(googleActions.changeGoogleAdsFormSelectType("select"));
    await dispatch(googleActions.setCheckedGoogleAdsFormItems(newCheckedItems));
    await dispatch(googleActions.setValidateGoogleLeadForm({ result: false }));
  };

  /** action when click select all */
  const handleSelectAll = () => {
    let newCheckedItems = [];
    setCheckedItems(newCheckedItems);
    dispatch(googleActions.setCheckedGoogleAdsFormItems(newCheckedItems));
  };

  const TooltipContent = (campaign) => {
    return (
      <>
        {campaign.length > 0 &&
          campaign.map((item, index) => (
            <TooltipContainer key={index} className="tooltip-container">
              <div className="campaign-name">{item.name}</div>
              <AdGroupContainer>
                {item.adGroup.map((adGroup, index) => (
                  <li key={index} className="adgroup-name">
                    {adGroup.name}
                  </li>
                ))}
              </AdGroupContainer>
            </TooltipContainer>
          ))}
      </>
    );
  };

  const FormContainer = () => {
    return (
      <FormListContainer errorStatus={errorStatus}>
        {googleAdsLeadFormResult.result === 0 && googleAdsLeadFormResult.msg === "GET_DATA_COMPLETED" ? (
          duplicateGoogleFormResult.data.duplicateGGForm.duplicate != null &&
          duplicateGoogleFormResult.data.duplicateGGForm.duplicate == "all" ? (
            <TextWarning>{t("__googleleads_google_form_used_all_form")}</TextWarning>
          ) : action == "add" && googleAdsLeadFormResult.formStatus == "ALLUSED" ? (
            <TextWarning>{t("__googleleads_google_form_used_all_form")}</TextWarning>
          ) : (
            <>
              {duplicateGoogleFormResult.data.duplicateGGForm.duplicate != null &&
              duplicateGoogleFormResult.data.duplicateGGForm.duplicate != "all" &&
              duplicateGoogleFormResult.data.duplicateGGForm.duplicate.length > 0
                ? null
                : googleAdsLeadFormResult.formStatus != "SOMEUSED" && (
                    <div className="radio-group">
                      <RadioAllButton>
                        <div
                          className={`radio-button ${selectedRadioValue === "all" ? "selected" : ""}`}
                          onClick={() => handleSelect("all")}
                        >
                          <div className={`radio-in-button ${selectedRadioValue === "all" ? "selected-in" : ""}`}></div>
                        </div>
                        <label className="cursor-pointer radio-all-label" htmlFor="fb_form_input_all">
                          <span>{t("__all")}</span>
                          <span className="label-describe">{t("__fbleads_fb_all_forn_describe")}</span>
                        </label>
                      </RadioAllButton>
                      <RadioButton>
                        <div
                          className={`radio-button ${selectedRadioValue === "select" ? "selected" : ""}`}
                          onClick={() => handleSelect("select")}
                        >
                          <div
                            className={`radio-in-button ${selectedRadioValue === "select" ? "selected-in" : ""}`}
                          ></div>
                        </div>
                        <label className="cursor-pointer" htmlFor="fb_form_input_custom">
                          {t("__custom")}
                        </label>
                      </RadioButton>
                    </div>
                  )}

              {googleAdsLeadFormResult.data.map((item, index) => {
                return (
                  <CheckBoxLabel
                    key={index}
                    formStatus={
                      googleAdsLeadFormResult.formStatus == "SOMEUSED" ||
                      duplicateGoogleFormResult.data.duplicateGGForm.duplicate != null
                    }
                  >
                    <MainLabel>
                      <input
                        className={`form-checkbox ${
                          selectedRadioValue == "all" ||
                          (googleAdsLeadFormResult.formStatus != "ALLUSED" &&
                            item.status == "USED" &&
                            item.google_leadform_setting_id != leadFormId) ||
                          (duplicateGoogleFormResult.data.duplicateGGForm.duplicate != null &&
                            duplicateGoogleFormResult.data.duplicateGGForm.duplicate != "all" &&
                            duplicateGoogleFormResult.data.duplicateGGForm.duplicate.includes(item.leadFormid))
                            ? ""
                            : "cursor-pointer "
                        }`}
                        id={`fb-form-input-${index}`}
                        type="checkbox"
                        name={`fb-form-input-${index}`}
                        onChange={(event) => handleOnChange(item.leadFormid, event)}
                        checked={checkedItems.includes(item.leadFormid)}
                        form="save_page"
                        disabled={
                          selectedRadioValue == "all" ||
                          (googleAdsLeadFormResult.formStatus != "ALLUSED" &&
                            item.status == "USED" &&
                            item.google_leadform_setting_id != leadFormId) ||
                          (duplicateGoogleFormResult.data.duplicateGGForm.duplicate != null &&
                            duplicateGoogleFormResult.data.duplicateGGForm.duplicate != "all" &&
                            duplicateGoogleFormResult.data.duplicateGGForm.duplicate.includes(item.leadFormid))
                        }
                      />
                      <label
                        className={`form-label ${
                          (googleAdsLeadFormResult.formStatus != "ALLUSED" &&
                            item.status == "USED" &&
                            item.google_leadform_setting_id != leadFormId) ||
                          (duplicateGoogleFormResult.data.duplicateGGForm.duplicate != null &&
                            duplicateGoogleFormResult.data.duplicateGGForm.duplicate != "all" &&
                            duplicateGoogleFormResult.data.duplicateGGForm.duplicate.includes(item.leadFormid))
                            ? "disabled-form"
                            : "cursor-pointer"
                        }`}
                        htmlFor={`fb-form-input-${index}`}
                      >
                        <GoogleFormLabel>
                          <span className="leadform-name">{item.leadFormName}</span>
                        </GoogleFormLabel>
                      </label>

                      <InfoIcon
                        src={
                          (googleAdsLeadFormResult.formStatus != "ALLUSED" &&
                            item.status == "USED" &&
                            item.google_leadform_setting_id != leadFormId) ||
                          (duplicateGoogleFormResult.data.duplicateGGForm.duplicate != null &&
                            duplicateGoogleFormResult.data.duplicateGGForm.duplicate != "all" &&
                            duplicateGoogleFormResult.data.duplicateGGForm.duplicate.includes(item.leadFormid))
                            ? "/images/info_disabled.svg"
                            : "/images/info.svg"
                        }
                        data-tip={windowSize.width <= 834 ? "custom show" : ""}
                        data-for={"tooltip-" + item.leadFormid}
                        data-event={windowSize.width <= 834 ? "click focus" : ""}
                        ref={infoRef}
                      />

                      <ReactTooltip
                        className="tooltip-style"
                        place={windowSize.width <= 834 ? tooltipPlace : "bottom"}
                        type="dark"
                        effect="solid"
                        id={"tooltip-" + item.leadFormid}
                        tooltipRadius="4"
                        globalEventOff={windowSize.width <= 834 ? "click" : ""}
                        getContent={() => TooltipContent(item.campaign)}
                      />
                    </MainLabel>
                    <SubLabel>
                      <div className="leadform-id">
                        {t("__form_id")}: {item.leadFormid}
                      </div>
                      {item.duplicate == true && <div className="leadform-describe">{item.leadFormDescription}</div>}
                      {((googleAdsLeadFormResult.formStatus != "ALLUSED" &&
                        item.status == "USED" &&
                        item.google_leadform_setting_id != leadFormId) ||
                        (duplicateGoogleFormResult.data.duplicateGGForm.duplicate != null &&
                          duplicateGoogleFormResult.data.duplicateGGForm.duplicate != "all" &&
                          duplicateGoogleFormResult.data.duplicateGGForm.duplicate.includes(item.leadFormid))) && (
                        <div className="form-describe">({t("__used")})</div>
                      )}
                    </SubLabel>
                  </CheckBoxLabel>
                );
              })}
            </>
          )
        ) : googleAdsLeadFormResult.result === 1 ||
          (googleAdsLeadFormResult.result === 0 && googleAdsLeadFormResult.msg === "NO_DATA") ? (
          <TextWarning>{t("__googleleads_google_form_not_found")}</TextWarning>
        ) : (
          <LoadingArea>
            <StyledLoadingIcon></StyledLoadingIcon>
          </LoadingArea>
        )}
      </FormListContainer>
    );
  };
  return <>{FormContainer()}</>;
};

export default GoogleAdsFormList;

const LoadingArea = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
  width: 100%;
  height: 180px;
`;

const StyledLoadingIcon = styled.div`
  border: 5px solid #ccc;
  border-top: 5px solid #eee;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  animation: button-keyframe-spin 1s linear infinite;
  box-sizing: initial !important;
  position: sticky;

  @keyframes button-keyframe-spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const FormListContainer = styled.div.attrs((props) => ({
  border: props.errorStatus ? `1px solid ${colors.negative}` : "1px solid #ddd",
}))`
  border: ${(props) => props.border};
  border-radius: 8px;
  margin: 0;
  padding: 10px 16px;
  height: 280px;
  overflow: auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  font-size: 14px;
  font-family: "Poppins", "Kanit", "Helvetica Neue", sans-serif;
  color: #555;

  .radio-group {
    position: relative;
  }

  & > *:not(:first-child) {
    margin-top: 10px;
  }
`;

const CheckBoxLabel = styled.div.attrs((props) => ({
  marginLeft: props.formStatus ? "0" : "24px",
}))`
  margin-left: ${(props) => props.marginLeft};
  display: flex;
  flex-direction: column;
`;

const RadioAllButton = styled.span`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  cursor: pointer;
  .radio-button {
    width: 13px;
    height: 13px;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid darkgrey;
    border-radius: 50%;
    top: 5px;
    margin-right: 10px;
  }
  .selected {
    border: 1px solid #1578f1;
  }

  .radio-in-button {
    width: 10px;
    height: 10px;
    position: relative;
    border: 1px solid #fff;
    border-radius: 50%;
    left: 0.2px;
  }
  .selected-in {
    background-color: #1578f1;
  }
  .radio-all-label {
    display: flex;
    flex-direction: column;
    line-height: 1rem;
    position: relative;
    top: 3px;
  }
  .label-describe {
    font-size: 12px;
    color: #aaa;
  }
  &:first-child {
    margin-right: 10px;
  }
`;

const RadioButton = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  .radio-button {
    width: 13px;
    height: 13px;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid darkgrey;
    border-radius: 50%;
    margin-right: 10px;
  }
  .selected {
    border: 1px solid #1578f1;
  }

  .radio-in-button {
    width: 10px;
    height: 10px;
    position: relative;
    border: 1px solid #fff;
    border-radius: 50%;
    left: 0.2px;
  }

  .selected-in {
    background-color: #1578f1;
  }

  &:first-child {
    margin-right: 10px;
  }
`;

const TextWarning = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: left;
  align-items: center;
  color: #aaa;
  font-size: 14px;
  font-weight: normal;
`;

const GoogleFormLabel = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
`;

const InfoIcon = styled.img`
  width: 14px;
  height: 14px;
  margin-left: 5px;
  position: relative;
  top: 6px;
`;

const TooltipContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 25px 0;
  .campaign-name {
    line-height: 1.5rem;
  }

  &:nth-child(2) {
    margin-top: 10px;
  }

  &:last-child {
    margin-bottom: 10px;
  }
`;

const AdGroupContainer = styled.ul`
  margin: 0;
  list-style-position: inside;
  .adgroup-name {
    white-space: wrap;
    word-break: break-word;
    text-indent: -1.2rem;
    line-height: 1.5rem;
  }
`;

const MainLabel = styled.div`
  display: flex;
  align-items: flex-start;
  flex-flow: row nowrap;
  .form-checkbox {
    position: relative;
    top: 4px;
    margin-right: 10px;
    flex-basis: fit-content;
  }
  .leadform-name {
    margin-right: 5px;
    word-break: break-word;
    line-height: 1.5rem;
  }
  .disabled-form {
    color: #aaa;
  }
  .tooltip-style {
    border-radius: 4px;
    opacity: 1 !important;
    max-width: 400px;
    line-height: 1.5rem;
  }
`;
const SubLabel = styled.div`
  display: flex;
  flex-flow: row wrap;
  margin-left: 27px;
  .leadform-id {
    font-size: 12px;
    color: #aaa;
    line-height: 1.5rem;
    margin-right: 5px;
  }
  .leadform-describe {
    font-size: 12px;
    color: #aaa;
    line-height: 1.5rem;
    margin-right: 5px;
  }

  .form-describe {
    font-style: italic;
    font-size: 12px;
    color: #aaa;
    line-height: 1.5rem;
  }
`;
